'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import useAuth from '~/hooks/useAuth';
import useModalManager from '~/hooks/useModalManager';
import { Button } from './ui';

export const CookieWrapper = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuth();
    const [hasAccepted, setHasAccepted] = useState<boolean | null>(null);
    const { addModal } = useModalManager();

    useEffect(() => {
        setHasAccepted(Boolean(localStorage.getItem('CookieInfoAccepted')));
    }, []);

    const handleAccept = () => {
        localStorage.setItem('CookieInfoAccepted', 'true');
        setHasAccepted(true);
    };

    const active = !hasAccepted && !auth.isAuthenticated;

    const handleOpenSettings = () => {
        addModal({
            title: 'Cookieeinstellungen',
            content: <CookieSettings />
        });
    };

    return (
        <>
            {children}
            <AnimatePresence>
                {active && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="tw-fixed tw-inset-0 tw-z-50 tw-bg-black/20 tw-backdrop-blur-lg"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="tw-fixed tw-bottom-2 tw-left-2 tw-right-2 tw-z-[1041] tw-rounded-2xl tw-bg-white tw-p-4 tw-shadow-xl md:tw-bottom-8 md:tw-left-8 md:tw-right-8 md:tw-p-6"
                        >
                            <h5 className="tw-text-md tw-mb-2 tw-font-semibold md:tw-text-2xl">
                                Wir schützen deine Daten!
                            </h5>
                            <p className="tw-mb-4 tw-text-sm tw-text-slate-700">
                                Wir verwenden Cookies und ähnliche Technologien, um Inhalte zu personalisieren, das
                                Nutzererlebnis zu optimieren sowie um Werbung anzupassen und zu bewerten. Indem du auf
                                OK klickst, wird dem zugestimmt. Um deine Einwilligung zu widerrufen, aktualisiere
                                einfach deine Cookie-Einstellungen.
                            </p>
                            <div className="tw-float-right tw-flex tw-items-baseline tw-gap-4">
                                <Button onClick={handleAccept}>Okay</Button>
                                <Button color="light" onClick={handleOpenSettings}>
                                    Einstellungen
                                </Button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

const CookieSettings = () => {
    return (
        <>
            <p>
                Wenn du eine Website besuchst, kann sie Informationen in deinem Browser speichern oder abrufen, meist in
                Form von Cookies. Diese Informationen beziehen sich möglicherweise auf dich, deine Einstellungen oder
                dein Gerät und werden hauptsächlich genutzt, damit die Website wie erwartet funktioniert. Diese Angaben
                identifizieren in der Regel nicht dich direkt, können dir aber ein personalisierteres Web-Erlebnis
                bieten. Das Blockieren einiger Arten von Cookies kann sich jedoch auf deine Nutzererfahrung und die
                Services auswirken, die wir anbieten können.
            </p>
            <hr className="tw-my-3" />
            <h5 className="tw-text-base tw-font-semibold">Unbedingt erforderliche Cookies</h5>
            <p>
                Diese Cookies sind für die Website notwendig und können in unseren Systemen nicht ausgeschaltet werden.
                Sie werden normalerweise als Reaktion auf eine deiner Aktionen eingestellt, die zu einer Anfrage nach
                einem Service führen wie bspw. das Einloggen oder das Ausfüllen von Formularen. Du kannst in deinem
                Browser die Blockierung von Cookies einstellen oder dich warnen lassen, jedoch könnten dann einige Teile
                der Website nicht funktionieren.
            </p>
            <ul className="tw-mt-3 tw-divide-y tw-divide-slate-200 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-pl-2">
                <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                    <span className="tw-text-sm tw-font-medium tw-text-slate-700">Sicherheit</span>
                    <input
                        type="checkbox"
                        className="tw-h-4 tw-w-4 tw-cursor-not-allowed tw-rounded tw-border-slate-300 tw-text-emerald-600"
                        checked
                        disabled
                        readOnly
                    />
                </li>
                <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                    <span className="tw-text-sm tw-font-medium tw-text-slate-700">Authentifizierung</span>
                    <input
                        type="checkbox"
                        className="tw-h-4 tw-w-4 tw-cursor-not-allowed tw-rounded tw-border-slate-300 tw-text-emerald-600"
                        checked
                        disabled
                        readOnly
                    />
                </li>
                <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                    <span className="tw-text-sm tw-font-medium tw-text-slate-700">Google API</span>
                    <input
                        type="checkbox"
                        className="tw-h-4 tw-w-4 tw-cursor-not-allowed tw-rounded tw-border-slate-300 tw-text-emerald-600"
                        checked
                        disabled
                        readOnly
                    />
                </li>
            </ul>
        </>
    );
};
